import { useQuery, useMutation, useQueryClient } from 'react-query';
import * as surveyMigrationApi from 'src/api/surveyMigration';
import { SurveyMigrationConfig } from 'src/api/surveyMigration';
import { UserStoreObject } from 'src/stores/UserStore';

const getOrgId = () => UserStoreObject.selectedOrg?.orgId;

export const surveyMigrationConfigKey = (orgId: number | undefined) => ['surveyMigrationConfig', orgId] as const;
export const surveyMigrationPreviewKey = (orgId: number | undefined) => ['surveyMigrationPreview', orgId] as const;
export const surveyMigrationStatusKey = (orgId: number | undefined) => ['surveyMigrationStatus', orgId] as const;

export const useSurveyMigrationConfig = () => {
    const orgId = getOrgId();
    return useQuery({
        queryKey: surveyMigrationConfigKey(orgId),
        queryFn: surveyMigrationApi.getConfig,
    });
};

export const useSaveSurveyMigrationConfig = () => {
    const queryClient = useQueryClient();
    const orgId = getOrgId();

    return useMutation({
        mutationFn: (config: SurveyMigrationConfig) => surveyMigrationApi.saveConfig(config),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: surveyMigrationConfigKey(orgId) });
        },
    });
};

export const useValidateSurveyMigrationConnection = () => {
    const queryClient = useQueryClient();
    const orgId = getOrgId();

    return useMutation({
        mutationFn: (config: SurveyMigrationConfig) => surveyMigrationApi.validateConnection(config),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: surveyMigrationConfigKey(orgId) });
        },
    });
};

export const useSurveyMigrationPreview = (enabled = false) => {
    const orgId = getOrgId();
    return useQuery({
        queryKey: surveyMigrationPreviewKey(orgId),
        queryFn: surveyMigrationApi.getPreview,
        enabled,
    });
};

export const useStartSurveyMigration = () => {
    const queryClient = useQueryClient();
    const orgId = getOrgId();

    return useMutation({
        mutationFn: surveyMigrationApi.startMigration,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: surveyMigrationStatusKey(orgId) });
            queryClient.invalidateQueries({ queryKey: surveyMigrationPreviewKey(orgId) });
        },
    });
};

const STATUS_POLL_INTERVAL_MS = 5000;

export const useSurveyMigrationStatus = (enabled = true) => {
    const orgId = getOrgId();
    return useQuery({
        queryKey: surveyMigrationStatusKey(orgId),
        queryFn: surveyMigrationApi.getStatus,
        enabled,
        // Poll only while the job is actively running; stop on
        // NOT_STARTED/PAUSED/COMPLETED/FAILED. Keep polling when job is undefined
        // (initial load or a transient fetch failure) so a failed first fetch
        // doesn't permanently stop polling.
        refetchInterval: (job) => {
            if (!job) {
                return STATUS_POLL_INTERVAL_MS;
            }
            return job.status === 'IN_PROGRESS' ? STATUS_POLL_INTERVAL_MS : false;
        },
    });
};

export const usePauseSurveyMigration = () => {
    const queryClient = useQueryClient();
    const orgId = getOrgId();

    return useMutation({
        mutationFn: surveyMigrationApi.pauseMigration,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: surveyMigrationStatusKey(orgId) });
        },
    });
};

export const useResumeSurveyMigration = () => {
    const queryClient = useQueryClient();
    const orgId = getOrgId();

    return useMutation({
        mutationFn: surveyMigrationApi.resumeMigration,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: surveyMigrationStatusKey(orgId) });
        },
    });
};

export const useDownloadSurveyMigrationErrors = () => {
    return useMutation({
        mutationFn: surveyMigrationApi.downloadErrors,
    });
};
